import type { TemplateBuilderConfig } from './template-builder-config';

/**
 * The active host config. Module-level (not React context) so the non-React
 * API clients can read it. `TemplateBuilderProvider` owns its lifecycle —
 * setting it on mount and clearing it on unmount.
 */
let activeConfig: TemplateBuilderConfig | null = null;

/**
 * Installs (or, with `null`, removes) the active host config. Called by
 * `TemplateBuilderProvider`.
 */
export function setConfig(config: TemplateBuilderConfig | null): void {
  activeConfig = config;
}

/** Removes the active config. Equivalent to `setConfig(null)`. */
export function clearConfig(): void {
  activeConfig = null;
}

/** Returns the active config, or `null` when none is installed. */
export function getConfigOrNull(): TemplateBuilderConfig | null {
  return activeConfig;
}

/**
 * Returns the active config, throwing when none is installed — which means the
 * code ran outside a `TemplateBuilderProvider`. Use from the API clients, where
 * a missing config is a programming error rather than a recoverable state.
 */
export function getConfig(): TemplateBuilderConfig {
  if (!activeConfig) {
    throw new Error(
      'Template Builder config is not set. Render the app inside <TemplateBuilderProvider config={…}>.',
    );
  }
  return activeConfig;
}
